'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/set-state-in-effect */
import { AnimatePresence, MotionDiv } from '@lib/motion';
import type { MenuSectionType } from '@lib/sanity/queries/menu/types';
import cn from '@utils/cn';
import { isDocumentDefined } from '@utils/validations';
import { HTMLAttributes, useEffect, useState } from 'react';

export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  menuSections?: MenuSectionType[];
  selectedSection?: MenuSectionType;
  desktopSections?: React.ReactElement[];
}
const divClassName = 'desktop-menu-section';
const MenuSectionDesktop = ({ menuSections, desktopSections, selectedSection, className, ...rest }: SectionProps) => {
  const [areaHeight, setAreaHeight] = useState<number>(0);

  const isSectionSelected = (section: MenuSectionType) => selectedSection?.title?.value === section.title?.value;

  function getActiveDiv() {
    if (!isDocumentDefined) {
      return;
    }
    const elementList = document.getElementsByClassName(divClassName);
    if (!elementList?.length) {
      return;
    }

    for (let i = 0; i < elementList.length; i++) {
      if (!(elementList[i] as HTMLDivElement).classList.contains('hidden')) {
        return elementList[i];
      }
    }
    return setAreaHeight(0);
  }

  useEffect(() => {
    const element = getActiveDiv();
    if (!element) {
      return;
    }

    const resizeOb = new ResizeObserver(() => {
      const element = getActiveDiv();
      if (!element || !selectedSection) {
        return;
      }
      setAreaHeight(element.clientHeight ?? 0);
    });
    resizeOb.observe(element);
    return () => resizeOb.disconnect();
  }, [selectedSection]);

  return (
    <AnimatePresence>
      <div className={cn('absolute h-screen w-full bg-black/50', { hidden: !selectedSection }, className)}>
        <div {...rest}>
          <MotionDiv
            initial={{ height: 0 }}
            animate={{
              height: areaHeight,
              transition: { duration: 0.2, delay: 0.07 },
            }}
            exit={{ height: 0, transition: { duration: 0.1 } }}
            className="bg-background overflow-hidden md:px-4 lg:px-0"
          >
            {menuSections?.map((section, i) => (
              <MotionDiv
                key={`${section.title?.value ?? ''}_${i}`}
                initial={{ opacity: 0.5 }}
                animate={{
                  opacity: isSectionSelected(section) ? 1 : 0.5,
                  transition: { delay: 0.1, duration: 0.2 },
                }}
                className={cn('relative z-50', divClassName, {
                  hidden: !isSectionSelected(section),
                })}
              >
                {desktopSections?.[i]}
              </MotionDiv>
            ))}
          </MotionDiv>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default MenuSectionDesktop;
